import {uniqueId} from 'lodash';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {CancelToken} from '../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {
  delayAsync as defaultDelayAsync,
  PromiseCompletionSource,
  TimeoutCallback,
  withTimeoutAsync as defaultWithTimeoutAsync,
} from '../../libs/shared/helpers/src/utilities/asyncHelpers';

export function delayAsync(delay: number = 0, cancelToken?: CancelToken): Promise<void> {
  return defaultDelayAsync(globalWindow, delay, cancelToken);
}

/*
 * Timeout.
 */

export async function withTimeoutAsync<T>(
  callback: () => Promise<T>,
  timeout: number,
  timeoutCallback?: TimeoutCallback<T>,
): Promise<T> {
  return defaultWithTimeoutAsync(globalWindow, callback, timeout, timeoutCallback);
}

/*
 * Jitter.
 */

/** Jitters the given delay. A jitter factor of 0 means no jitter, 1 means full jitter. */
export function computeDelayWithJitter(delay: number, jitterWeight: number): number {
  if (jitterWeight < 0 || jitterWeight > 1) {
    throw new Error('Invalid jitter factor');
  }

  const nonJitterWeight = 1 - jitterWeight;
  const jitter = Math.random();

  return nonJitterWeight * delay + jitterWeight * delay * jitter;
}

export function delayWithJitterAsync(delay: number, jitterWeight: number, cancelToken?: CancelToken) {
  return delayAsync(computeDelayWithJitter(delay, jitterWeight), cancelToken);
}

/*
 * Buffer.
 */

export type AsyncBufferWaiter = Promise<{isLastWaiter: boolean}>;

export class AsyncBuffer {
  constructor() {
    this.waiterSource = new PromiseCompletionSource();
    this.wasFlushed = false;
  }

  private readonly waiterSource: PromiseCompletionSource<void>;
  private lastWaiterId?: string;
  private wasFlushed: boolean;

  async buildWaiter(): AsyncBufferWaiter {
    if (this.wasFlushed) {
      throw new Error('This buffer was already flushed.');
    }

    const waiterId = uniqueId('waiterid-');
    this.lastWaiterId = waiterId;

    await this.waiterSource.promise;
    return {isLastWaiter: waiterId === this.lastWaiterId};
  }

  flush() {
    if (this.wasFlushed) {
      throw new Error('This buffer was already flushed.');
    }

    this.wasFlushed = true;

    if (!this.lastWaiterId) {
      return;
    }

    this.waiterSource.resolve();
  }
}
